<script setup lang="ts">
import { Head, Link } from "@inertiajs/vue3"
import { Form, router } from "@inertiajs/vue3"
import { computed, ref, toRaw } from "vue"

import Heading from "@/components/Heading.vue"
import ComboBox from "@/components/custom/ComboBox.vue"
import DatePicker from "@/components/custom/DatePicker.vue"
import FormField from "@/components/custom/FormField.vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout.vue"
import { managerEmployeePath, managerEmployeesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { ManagerEmployeesEdit } from "@/types/serializers"

const props = defineProps<ManagerEmployeesEdit>()

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Perfiles de Empleados",
    href: managerEmployeesPath(),
  },
  {
    title: `${props.employee.ref || props.employee.id}`,
    href: managerEmployeePath(props.employee.id),
  },
  {
    title: "Editar empleado",
    href: managerEmployeePath(props.employee.id),
  },
]

const formData = ref(structuredClone(toRaw(props.employee)))

const departmentOptions = computed(() =>
  props.library.departments.map((department) => ({
    value: department,
    label: department,
  })),
)

const jobTitleOptions = computed(() =>
  props.library.job_titles.map((jobTitle) => ({
    value: jobTitle,
    label: jobTitle,
  })),
)
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="px-6 py-4">
      <Heading
        :title="`Editar empleado`"
        description="Actualiza la informacion principal del empleado"
      />

      <Form
        class="mt-6 max-w-4xl space-y-6"
        method="PUT"
        disable-while-processing
        :action="managerEmployeePath(employee.id)"
        :options="{
          preserveScroll: true,
          preserveState: true,
        }"
        #default="{ processing, errors }"
        @success="router.visit(managerEmployeePath(props.employee.id))"
      >
        <div class="grid gap-4 *:col-span-4 sm:grid-cols-12">
          <FormField label="DNI" class="col-span-2!" :errors="errors.dni">
            <Input v-model="formData.dni" name="dni" disabled />
          </FormField>

          <FormField
            label="Nombre"
            class="col-span-4!"
            :errors="errors.first_name"
          >
            <Input v-model="formData.first_name" name="first_name" disabled />
          </FormField>

          <FormField
            label="Apellidos"
            class="col-span-6!"
            :errors="errors.last_name"
          >
            <Input v-model="formData.last_name" name="last_name" disabled />
          </FormField>

          <FormField
            label="Colectivo"
            class="col-span-6!"
            :errors="errors.first_contract"
          >
            <Input
              v-model="formData.first_contract"
              name="first_contract"
              placeholder="Ej: COMERCIA"
              disabled
            />
          </FormField>

          <FormField
            label="Nacimiento"
            class="col-span-2!"
            :errors="errors.birthdate"
          >
            <DatePicker
              v-model="formData.birthdate"
              name="birthdate"
              disabled
            />
          </FormField>

          <FormField
            label="Fecha inicio"
            class="col-span-2!"
            :errors="errors.start_date"
          >
            <DatePicker
              v-model="formData.start_date"
              name="start_date"
              disabled
            />
          </FormField>

          <FormField
            label="Fecha fin"
            class="col-span-2!"
            :errors="errors.termination_date"
          >
            <DatePicker
              v-model="formData.termination_date"
              name="termination_date"
              disabled
            />
          </FormField>

          <FormField label="Ref" class="col-span-2!" :errors="errors.ref">
            <Input v-model="formData.ref" name="ref" />
          </FormField>

          <FormField
            label="Nombre preferido"
            class="col-span-4!"
            :errors="errors.preferred_name"
          >
            <Input v-model="formData.preferred_name" name="preferred_name" />
          </FormField>

          <FormField label="Email" class="col-span-6!" :errors="errors.email">
            <Input v-model="formData.email" type="email" name="email" />
          </FormField>

          <FormField label="Departamento" :errors="errors.department">
            <ComboBox
              full-width
              name="department"
              v-model="formData.department"
              :options="departmentOptions"
              placeholder="Selecciona departamento"
              search-placeholder="Busca o escribe departamento"
            />
          </FormField>

          <FormField label="Job title" :errors="errors.job_title">
            <ComboBox
              full-width
              v-model="formData.job_title"
              name="job_title"
              :options="jobTitleOptions"
              placeholder="Selecciona job title"
              search-placeholder="Busca o escribe job title"
            />
          </FormField>

          <FormField label="Role" class="col-span-4!" :errors="errors.role">
            <Select name="role" v-model="formData.role" :disabled="processing">
              <SelectTrigger class="w-full">
                <SelectValue placeholder="Selecciona rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem :value="null">Employee</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <div class="flex items-center gap-2">
          <Button type="submit" :disabled="processing">
            Guardar cambios
          </Button>
          <Button variant="outline" asChild>
            <Link :href="managerEmployeePath(employee.id)">Cancelar</Link>
          </Button>
        </div>
      </Form>
    </div>
  </AppLayout>
</template>
